import { Button, Form, Modal, Select } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { getEntityById, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import OWAsyncSelect, { OW_ASYNC_SELECT_DEFAULT_PAGINATION } from '../ow_async_select/OWAsyncSelect';
import { OWButton } from '../ow_button/OWButton';
import { useForm } from 'antd/lib/form/Form';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import { equipmentTypesRestCrudThunksForSupplier } from '../../thunks/equipment_types_thunks';
import {
	fetchSLWithEquipmentCount,
	stockLocationsRestCrudThunksForSupplier,
} from '../../thunks/stock_locations_thunks';
import { equipmentPerStockLocationsRestCrudThunksForSupplier } from '../../thunks/equipment_per_stock_locations_thunks';
import { EQUIPMENT_PER_STOCK_LOCATIONS_CRUD_ACTION_CREATORS } from '../../actions/equipment_per_stock_locations_actions';
import { PERMISSION_NAMES, isSupplierAllowedToAccess } from '../../utils/AuthUtils';
import { addEquipmentToWorkOrder } from '../../thunks/parts_equipments_work_orders_thunks';
import { ROLE_TYPES } from '../../utils/DataConstants';

const STOCK_LOCATION_ASSOCIATED_EQUIPMENTS_TC = 'equipmentsIndex';
const EQUIPMENT_PER_STOCK_LOCATION_DROPDOWN = 'stockLocationAssociatedEquipments';

const AddEquipmentModal = ({
	equipmentTypes,
	createEquipment,
	onCancel,
	addEquipmentToWO,
	successCallback,
	serviceCall,
	equipmentInServiceCall,
	openCreatePurchaseRequest,
	currentUser,
	woStockLocationId,
	fetchEquipmentTypes,
	fetchEquipmentsPerStockLocation,
	fetchMultipleEquipmentsPerStockLocation,
	equipmentsPerStockLocation,
	clearEquipmentPerStockLocationTargetCollection,
	epslFetching,
	stockLocations,
	fetchStockLocationsWithEquipmentCount,
	fetchMultipleStockLocations,
	companyConfig,
	match,
}): React.ReactElement => {
	const [form] = useForm();

	const { setFields } = form;

	useEffect(() => {
		clearEquipmentPerStockLocationTargetCollection(EQUIPMENT_PER_STOCK_LOCATION_DROPDOWN);
	}, []);

	const workOrderId = useMemo(() => match.params.id, [match.params.id]);

	const equipmentPerStockLocationId = Form.useWatch('equipmentPerStockLocationId', form);
	const equipmentTypeId = Form.useWatch('equipmentTypeId', form);
	const stockLocationId = Form.useWatch('stockLocationId', form);

	const [saving, setSaving] = useState(false);

	useEffect(() => {
		equipmentTypeId &&
			stockLocationId &&
			fetchEquipmentsPerStockLocation(
				{
					equipmentTypeId,
					stockLocationId,
				},
				EQUIPMENT_PER_STOCK_LOCATION_DROPDOWN,
				OW_ASYNC_SELECT_DEFAULT_PAGINATION
			);
	}, [equipmentTypeId, fetchEquipmentsPerStockLocation, stockLocationId]);

	useEffect(() => {
		equipmentTypeId &&
			fetchStockLocationsWithEquipmentCount(
				equipmentTypeId,
				{},
				'woEquipmentSLIndex',
				OW_ASYNC_SELECT_DEFAULT_PAGINATION
			);
	}, [equipmentTypeId, fetchStockLocationsWithEquipmentCount]);

	const equipmentRecord = useMemo(() => {
		const equipmentRecords = getRecordsForTargetCollection(
			equipmentTypes,
			STOCK_LOCATION_ASSOCIATED_EQUIPMENTS_TC
		);
		return equipmentRecords.find((_) => _.id === equipmentTypeId);
	}, [equipmentTypes, equipmentTypeId]);

	const supplierCompanyId = useMemo(
		() => nullSafeGet('facility.supplierCompanyId', currentUser),
		[currentUser]
	);

	const supplierFacilityId = useMemo(() => nullSafeGet('facility.id', currentUser), [currentUser]);

	const createEquipmentWithSLResponse = useCallback(
		(modelName) => {
			return new Promise((resolve, reject) => {
				createEquipment({
					modelName,
					supplierCompanyId,
					supplierFacilityId,
					images: [],
				})
					.then((equipmentType) =>
						resolve({
							id: equipmentType.id,
							equipmentTypeNumber: equipmentType.equipmentTypeNumber,
							equipmentType,
						})
					)
					.catch((err) => reject(err));
			});
		},
		[supplierCompanyId, supplierFacilityId, createEquipment]
	);

	const initialValue = useMemo(
		() => ({
			serviceCallId: nullSafeGet('id', serviceCall),
			equipmentTypeId: nullSafeGet('partId', equipmentInServiceCall),
			equipmentPerStockLocationId: nullSafeGet(
				'equipmentPerStockLocationId',
				equipmentInServiceCall
			),
			stockLocationId:
				nullSafeGet('equipmentPerStockLocation.stockLocationId', equipmentInServiceCall) ||
				woStockLocationId,
		}),
		[serviceCall, equipmentInServiceCall, woStockLocationId]
	);

	const onSubmit = useCallback(
		(values) => {
			if (!values.equipmentPerStockLocationId) return;
			setSaving(true);
			addEquipmentToWO({
				workOrderId,
				equipmentPerStockLocationId: values.equipmentPerStockLocationId,
			})
				.then(() => successCallback && successCallback())
				.finally(() => setSaving(false));
		},
		[addEquipmentToWO, workOrderId, successCallback]
	);

	const openCreatePurchaseOrder = useCallback(() => {
		openCreatePurchaseRequest(equipmentRecord, stockLocationId);
	}, [stockLocationId, equipmentRecord, openCreatePurchaseRequest]);

	const isNoStockLocationFound = useMemo(
		() =>
			!epslFetching &&
			getRecordsForTargetCollection(
				equipmentsPerStockLocation,
				EQUIPMENT_PER_STOCK_LOCATION_DROPDOWN
			).length < 1,
		[equipmentsPerStockLocation, epslFetching]
	);

	const showCreatePOButton = useMemo(
		() => equipmentTypeId && stockLocationId && isNoStockLocationFound,
		[equipmentTypeId, isNoStockLocationFound, stockLocationId]
	);

	useEffect(() => {
		setFields([
			{
				name: 'equipmentPerStockLocationId',
				value: equipmentPerStockLocationId,
				errors: showCreatePOButton
					? ['Selected equipment is not available in any stock location!']
					: [],
			},
		]);
	}, [isNoStockLocationFound, setFields, equipmentPerStockLocationId, showCreatePOButton]);

	const primaryActionButton = useMemo(() => {
		return showCreatePOButton ? (
			<Button
				type="primary"
				size="large"
				style={{ marginLeft: '16px' }}
				loading={saving}
				onClick={openCreatePurchaseOrder}
			>
				Request Equipment
			</Button>
		) : (
			<OWButton
				type="primary"
				size="large"
				style={{ marginLeft: '16px' }}
				key="submit"
				htmlType="submit"
				form="add-parts-form"
				loading={saving}
			>
				Add
			</OWButton>
		);
	}, [showCreatePOButton, saving, openCreatePurchaseOrder]);

	const canCreatePartOrEquipment = useMemo(
		() =>
			isSupplierAllowedToAccess(
				PERMISSION_NAMES.MODIFY_PARTS_AND_EQUIPMENTS,
				companyConfig,
				currentUser.roles
			),
		[companyConfig, currentUser.roles]
	);

	return (
		<Modal
			visible={true}
			width={600}
			title="Add equipment"
			closable
			onCancel={onCancel}
			forceRender
			footer={[
				<Button onClick={onCancel} size="large">
					Cancel
				</Button>,
				primaryActionButton,
			]}
		>
			<Form
				form={form}
				id="add-parts-form"
				layout="vertical"
				requiredMark={false}
				initialValues={initialValue}
				onFinish={onSubmit}
			>
				<Form.Item name="serviceCallId" hidden />
				<Form.Item name="equipmentTypeId" label="Equipment">
					<OWAsyncSelect
						stateSlice={equipmentTypes}
						targetCollectionName={STOCK_LOCATION_ASSOCIATED_EQUIPMENTS_TC}
						labelAccessor={(el) => nullSafeGet('modelName', el)}
						fetchData={(
							searchText,
							targetCollectionName,
							pagination,
							sorting,
							filters,
							addToTargetCollection
						) =>
							fetchEquipmentTypes(
								{
									search: searchText || undefined,
								},
								targetCollectionName,
								pagination,
								sorting,
								filters,
								addToTargetCollection
							)
						}
						fetchMultiple={() => {}}
						createNewEntity={
							canCreatePartOrEquipment ? (name) => createEquipmentWithSLResponse(name) : undefined
						}
						renderRecord={(equipmentType) => (
							<Select.Option key={equipmentType.id} value={equipmentType.id}>
								<div className="flex flex-row justify-between">
									{equipmentType.equipmentUniqueId ? `${equipmentType.equipmentTypeId} - ` : null}{' '}
									<div className="flex flex-row items-center">
										{equipmentType.modelName}
										{equipmentType.equipmentTypeNumber && (
											<div className="text-gray-400">
												&nbsp;{` - #${equipmentType.equipmentTypeNumber}`}
											</div>
										)}
									</div>
								</div>
							</Select.Option>
						)}
						sortBy={{ sort_by: 'name', order: 'ascend' }}
					/>
				</Form.Item>
				<Form.Item
					name="stockLocationId"
					label="Stock Location"
					rules={[
						{
							required: true,
							message: 'Please select a stock location!',
						},
					]}
				>
					<OWAsyncSelect
						disabled={!equipmentTypeId}
						stateSlice={stockLocations}
						valueAccessor={(el) => el.id}
						targetCollectionName={'woEquipmentSLIndex'}
						labelAccessor={(el) => nullSafeGetOrElse('name', el, '')}
						fetchData={(
							searchText,
							targetCollectionName,
							pagination,
							sorting,
							filters,
							addToTargetCollection
						) => {
							equipmentTypeId &&
								fetchStockLocationsWithEquipmentCount(
									equipmentTypeId,
									{ searchText },
									targetCollectionName,
									pagination,
									sorting,
									filters,
									addToTargetCollection
								);
						}}
						fetchMultiple={(ids, targetCollectionName) => {
							fetchMultipleStockLocations(ids, targetCollectionName);
						}}
						renderRecord={(sl) => (
							<Select.Option key={sl.id} value={sl.id}>
								<div className="flex flex-row justify-between">
									<div>{nullSafeGet('name', sl)}</div>
									<div>{`(${nullSafeGetOrElse('count', sl, 0)})`}</div>
								</div>
							</Select.Option>
						)}
					/>
				</Form.Item>
				<Form.Item
					name="equipmentPerStockLocationId"
					label="Serial Number"
					rules={[
						{
							required: true,
							message: 'Please select a serial number!',
						},
					]}
				>
					<OWAsyncSelect
						disabled={!(equipmentTypeId && stockLocationId)}
						stateSlice={equipmentsPerStockLocation}
						valueAccessor={(el) => el.id}
						targetCollectionName={EQUIPMENT_PER_STOCK_LOCATION_DROPDOWN}
						labelAccessor={(epsl) => {
							nullSafeGetOrElse('serialNumber', epsl, '-');
						}}
						fetchData={(
							searchText,
							targetCollectionName,
							pagination,
							sorting,
							filters,
							addToTargetCollection
						) => {
							equipmentTypeId &&
								stockLocationId &&
								fetchEquipmentsPerStockLocation(
									{
										searchText,
										equipmentTypeId,
										stockLocationId,
										isDisbursed: false,
									},
									targetCollectionName,
									pagination,
									sorting,
									filters,
									addToTargetCollection
								);
						}}
						fetchMultiple={(ids, targetCollectionName) => {
							fetchMultipleEquipmentsPerStockLocation(ids, targetCollectionName);
						}}
						renderRecord={(epsl) => (
							<Select.Option key={epsl.id} value={epsl.id}>
								{nullSafeGetOrElse('serialNumber', epsl, '-')}
							</Select.Option>
						)}
					/>
				</Form.Item>
			</Form>
		</Modal>
	);
};

const mapStateToProps = (state, ownProps) => ({
	equipmentTypes: state.equipment_types,
	workOrder: getEntityById(ownProps.match.params.id, state.work_orders),
	stockLocations: state.stock_locations,
	equipmentsPerStockLocation: state.equipment_per_stock_locations,
	epslFetching: state.equipment_per_stock_locations.fetching,
	currentUser: state.session.currentUser,
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch) => ({
	createEquipment: (entity) => dispatch(equipmentTypesRestCrudThunksForSupplier.create(entity)),
	fetchEquipmentTypes: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			equipmentTypesRestCrudThunksForSupplier.read(
				{ ...(params || {}), isActive: true },
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchStockLocationsWithEquipmentCount: (
		equipmentTypeId,
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			fetchSLWithEquipmentCount(ROLE_TYPES.SUPPLIER)(
				equipmentTypeId,
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchMultipleStockLocations: (ids, targetCollectionName) =>
		dispatch(stockLocationsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	clearEquipmentPerStockLocationTargetCollection: (tcName) =>
		dispatch(EQUIPMENT_PER_STOCK_LOCATIONS_CRUD_ACTION_CREATORS.clearTargetCollection(tcName)),
	fetchEquipmentsPerStockLocation: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			equipmentPerStockLocationsRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	addEquipmentToWO: (entity) => dispatch(addEquipmentToWorkOrder(ROLE_TYPES.SUPPLIER)(entity)),
	fetchMultipleEquipmentsPerStockLocation: (ids, targetCollectionName) =>
		dispatch(stockLocationsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
});

export default withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(AddEquipmentModal)
);
